import * as moment from 'moment';
import { yesNo, yn, existsOrBlank } from './mapping-tools';
import { stripPhone, cleanDate } from './formatting';
import getAgentID from './getAgentID';

export default ({
  agent,
  plan,
  quote,
  fields: res,
  theme,
  confirmationNumber,
}) => {
  const { useCarrierPerms } = theme;

  let agentID;
  if (useCarrierPerms) {
    // wellcare uses NPN as their agent id, so we popualte agentID with npn through the RTS process
    const ids = getAgentID({ agent, quote });
    agentID = ids.agentID;
  }

  const premiumDeducted = yesNo(['rrb', 'ssi'].includes(res.paymeth));
  const premiumDirectPay = yesNo(!['rrb', 'ssi'].includes(res.paymeth)); // if paymeth not specified this is set to true
  const premiumWithhold = ['rrb', 'ssi'].includes(res.paymeth) ? res.paymeth.toUpperCase() : '';
  const planYear = plan.PlanYear;
  const isEFT = res.paymeth === 'eft';
  const eftActType = (res.eft_acttype === 'checking') ? 'Checking' : 'Saving';
  const ltcfAddress = res.ltcf
    ? `${res.ltcf_addr_street1}${res.ltcf_addr_street2 ? ` ${res.ltcf_addr_street2}` : ''}, ${res.ltcf_addr_city}, ${res.ltcf_addr_state} ${res.ltcf_addr_zip}`
    : '';

  const isSEP = res.enrolltype === 'SEP';
  const isSepOther = res.sep_code === 'OTH';
  // format sep date as mmddyyyy
  const sepDate = res.sep_date ? `${res.sep_date.slice(5, 7)}${res.sep_date.slice(8, 10)}${res.sep_date.slice(0, 4)}` : '';
  const sepConcat = `${res.sep_code} ${sepDate}`.trim();

  const effectiveDate = moment(res.effective_date).format('MMDDYYYY');
  const agentFullName = `${agent.firstName} ${agent.lastName}`;
  const gender = (res.gender === 'male') ? 'M' : 'F';

  const premiumAmount = quote.premium;
  // const isLargePrint = yn(res.altwritten === 'LargePrint');

  let race = '';
  let ethnicity = '';

  if (Array.isArray(res.race) && res.race.length) {
    race = res.race.join(',');
  }
  if (Array.isArray(res.ethnicity) && res.ethnicity.length) {
    ethnicity = res.ethnicity.join(',');
  }

  const formattedData = [
    { id: '01', col: 'ConfirmationNumber', val: confirmationNumber },
    { id: '02', col: 'SubmitDate', val: moment().format('MMDDYYYY') },
    { id: '03', col: 'ContractID', val: plan.ExternalContractID },
    { id: '04', col: 'PlanID', val: plan.ExternalPlanID },
    { id: '05', col: 'SegmentID', val: plan.ExternalSegmentID },
    { id: '06', col: 'ApplicantTitle', val: '' },
    { id: '07', col: 'ApplicantFirstName', val: res.first_name },
    { id: '08', col: 'ApplicantMiddleInitial', val: res.middle_name },
    { id: '09', col: 'ApplicantLastName', val: res.last_name },
    { id: '10', col: 'ApplicantBirthDate', val: moment(res.birthdate).format('MMDDYYYY') },
    { id: '11', col: 'ApplicantGender', val: gender },
    { id: '12', col: 'ApplicantAddress1', val: res.street1 },
    { id: '13', col: 'ApplicantAddress2', val: res.street2 },
    { id: '14', col: 'ApplicantAddress3', val: '' },
    { id: '15', col: 'ApplicantCity', val: (res.city || '').slice(0, 30) },
    { id: '16', col: 'ApplicantCounty', val: res.county_name },
    { id: '17', col: 'ApplicantState', val: res.state.toUpperCase() },
    { id: '18', col: 'ApplicantZip', val: res.zipcode },
    { id: '19', col: 'PrimaryPhoneNumber', val: stripPhone(res.home_phone) },
    { id: '20', col: 'ApplicantEmailAddress', val: res.email },
    { id: '21', col: 'ApplicantHICN', val: res.medicare_id },
    { id: '22', col: 'ApplicantSSN', val: '' }, // we do not collect ssn
    { id: '23', col: 'MailingAddress1', val: res.samemail ? res.street1 : res.mailstreet1 },
    { id: '24', col: 'MailingAddress2', val: res.samemail ? res.street2 : res.mailstreet2 },
    { id: '25', col: 'MailingAddress3', val: '' },
    { id: '26', col: 'MailingCity', val: res.samemail ? res.city : res.mailcity },
    { id: '27', col: 'MailingState', val: res.samemail ? res.state : res.mailstate },
    { id: '28', col: 'MailingZip', val: res.samemail ? res.zipcode : res.mailzip },
    { id: '29', col: 'MedicarePartA', val: cleanDate(res.part_a_effective_date) },
    { id: '30', col: 'MedicarePartB', val: cleanDate(res.part_b_effective_date) },
    { id: '31', col: 'EmergencyContact', val: res.hasemer ? `${res.emerfname} ${res.emerlname}` : '' },
    { id: '32', col: 'EmergencyPhone', val: res.hasemer ? stripPhone(res.emertel) : '' },
    { id: '33', col: 'EmergencyRelationship', val: res.hasemer ? res.emerrel : '' },
    { id: '34', col: 'PremiumDeducted', val: premiumDeducted },
    { id: '35', col: 'PremiumSource', val: '' },
    { id: '36', col: 'OtherCoverage', val: yn(res.othercoverage) }, // applies to PDP, MAPD, SNP DE, PFFS-PD, CP-PD
    { id: '37', col: 'OtherCoverageName', val: res.othercoverage ? res.othercoverage_name : '' },
    { id: '38', col: 'OtherCoverageID', val: res.othercoverage ? res.othercoverage_num : '' },
    { id: '39', col: 'LongTerm', val: yn(res.ltcf) },
    { id: '40', col: 'LongTermName', val: res.ltcf ? res.ltcf_name : '' },
    { id: '41', col: 'LongTermAddress', val: ltcfAddress },
    { id: '42', col: 'LongTermPhone', val: res.ltcf ? stripPhone(res.ltcf_tel) : '' },
    { id: '43', col: 'AuthorizedRepName', val: res.ispoa ? `${res.poa_fname} ${res.poa_lname}` : '' },
    { id: '44', col: 'AuthorizedRepAddress', val: res.ispoa ? `${res.poa_addr_street1} ${res.poa_addr_street2 || ''}`.trim() : '' },
    { id: '45', col: 'AuthorizedRepCity', val: res.ispoa ? res.poa_addr_city : '' },
    { id: '46', col: 'AuthorizedRepState', val: res.ispoa ? res.poa_addr_state : '' },
    { id: '47', col: 'AuthorizedRepZip', val: res.ispoa ? res.poa_addr_zip : '' },
    { id: '48', col: 'AuthorizedRepPhone', val: res.ispoa ? res.poa_tel : '' },
    { id: '49', col: 'AuthorizedRepRelationship', val: res.ispoa ? res.poa_rel : '' },
    { id: '50', col: 'Language', val: res.preflang ? res.preflang : '' },
    { id: '51', col: 'ESRD', val: '' }, // Per CMS as of 12/31/2020 ESRD no longer applicable (End-Stage Renal Disease)
    { id: '52', col: 'StateMedicaid', val: yn(res.onmedicaid) },
    { id: '53', col: 'WorkStatus', val: yn(res.willwork || res.willworkspouse) },
    { id: '54', col: 'PrimaryCarePhysicianName', val: res.pcp_name || '' },
    { id: '55', col: 'OtherCoverageGroup', val: res.othercoverage_group },
    { id: '56', col: 'AgentID', val: agentID },
    { id: '57', col: 'SubmitTime', val: moment().format() },
    { id: '58', col: 'PartDSubAppInd', val: '' }, // ignoring because opt (and we don't collect subsidy info)
    { id: '59', col: 'DeemedInd', val: '' }, // ignoring because opt (and we don't collect subsidy info)
    { id: '60', col: 'SubsidyPercentage', val: '' }, // ignoring because opt (and we don't collect subsidy info)
    { id: '61', col: 'DeemedReasonCode', val: '' }, // ignoring because opt (and we don't collect subsidy info)
    { id: '62', col: 'LISCopayLevelID', val: '' }, // ignoring because opt (and we don't collect subsidy info)
    { id: '63', col: 'DeemedCopayLevelID', val: '' }, // ignoring because opt (and we don't collect subsidy info)
    { id: '64', col: 'PartDOptOutSwitch', val: '' }, // ignoring because opt (and we don't collect subsidy info)
    // formatted as `SEPCODE mmddyyyy` or `SEPCODE`
    { id: '65', col: 'SEPReasonCode', val: isSEP ? sepConcat : '' },
    // Only used when indicating OTH as the SEP Reason Code in order to explain the reason the individual is eligible to enroll
    { id: '66', col: 'SEPCMSReasonCode', val: isSepOther ? res.sep_other : '' },
    { id: '67', col: 'PremiumDirectPay', val: premiumDirectPay },
    { id: '68', col: 'EnrollmentPlanYear', val: planYear },
    { id: '69', col: 'RequestedEffectiveDate', val: effectiveDate },
    { id: '70', col: 'PremiumWithhold', val: premiumWithhold },
    { id: '71', col: 'PolicyPremiumAmount', val: premiumAmount },
    { id: '72', col: 'RoutingNumber', val: isEFT ? res.eft_routing : '' },
    { id: '73', col: 'BankName', val: isEFT ? res.eft_bankname : '' },
    { id: '74', col: 'AccountNumber', val: isEFT ? res.eft_actnum : '' },
    { id: '75', col: 'Accounttype', val: isEFT ? eftActType : '' },
    { id: '76', col: 'IsSignaturedifferentfromOwner', val: isEFT ? yn(res.eft_sigdiff) : '' },
    { id: '77', col: 'AccountholderName', val: isEFT ? res.eft_actname : '' },
    { id: '78', col: 'AccessibilityFormat', val: res.altwritten },
    { id: '79', col: 'EmailOptIn', val: yn(res.okemail) },
    { id: '80', col: 'AgentName', val: agentFullName },
    { id: '81', col: 'ProviderID', val: res.pcp_ipa || '' }, // wellcare IPA number goes here
    { id: '82', col: 'Currentpatient', val: yn(res.pcp_established) },
    { id: '83', col: 'CampaignCode', val: '' }, // not applicable to MMB
    { id: '84', col: 'Race', val: race },
    { id: '85', col: 'Ethnicity', val: ethnicity },

    { id: '86', col: 'PrimaryCarePhysicianID', val: res.pcp_num },
    { id: '87', col: 'PrimaryCarePhysicianNPI', val: '' },
    { id: '88', col: 'ProviderGroupName', val: '' },
    { id: '89', col: 'SignatureReceived', val: '' },
    { id: '90', col: 'SignatureDate', val: '' },
    { id: '91', col: 'SecondaryPhoneNumber', val: '' },
    { id: '92', col: 'MedicaidNumber', val: existsOrBlank(res.medicaidnum) },
    { id: '93', col: 'TextOptin', val: '' },
    { id: '94', col: 'SourceOfApplication', val: '' },
    { id: '95', col: 'BuyUpPackage', val: '' },
    { id: '96', col: 'PreferredMethodofContact', val: res.prefcontactmethod || '' },

    // new fields added in 2025
    { id: '97', col: 'PrimaryPhoneType', val: '' },
    { id: '98', col: 'SecondaryPhoneType', val: '' },
    { id: '99', col: 'HomelessIndicator', val: '' },
    { id: '100', col: 'GenderIdentity', val: '' }, // SOGI questions retroactively removed from forms, don't populate
    { id: '101', col: 'GenderIdentityOther', val: '' }, // SOGI questions retroactively removed from forms, don't populate
    { id: '102', col: 'SelfIdentify', val: '' }, // SOGI questions retroactively removed from forms, don't populate
    { id: '103', col: 'SelfIdentifyOther', val: '' }, // SOGI questions retroactively removed from forms, don't populate
    { id: '104', col: 'IndividualRepName', val: agentFullName },
    { id: '105', col: 'IndividualRepRelationship', val: res.agent_relationship },
    { id: '106', col: 'NationalProducerNumber', val: agentID },
  ];

  return { formattedData, agentID };
};
